import {
    Box, Button, Chip, Divider, Grid, Paper, Skeleton, Stack,
    Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import {useMemo} from "react";
import {ObservationReportStatsResponse, OfficerReportCount} from "../../data/ObservationReportStatsResponse";

function StatCard({label, value, color, tooltip}: {label: string, value: string, color?: string, tooltip?: string}) {
    const inner = <Paper variant="outlined" sx={{p: 1.5, textAlign: "center"}}>
        <Typography variant="h5" fontWeight={700} color={color ?? "primary.main"}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
    return tooltip ? <Tooltip title={tooltip}>{inner}</Tooltip> : inner
}

function fmt(value: number | null, decimals = 2): string {
    if (value === null) return "N/A"
    return value.toFixed(decimals)
}

interface ObservationStatsPanelProps {
    stats: ObservationReportStatsResponse | null
    isLoading: boolean
    minimum: number
    onMinimumChange: (v: number) => void
    prioritiseNotMet: boolean
    onPrioritiseNotMetChange: (v: boolean) => void
    onExport: () => void
    exportDisabled: boolean
}

export function ObservationStatsPanel({stats, isLoading, minimum, onMinimumChange, prioritiseNotMet, onPrioritiseNotMetChange, onExport, exportDisabled}: ObservationStatsPanelProps) {
    const hasMinimum = minimum > 0

    // Supervisor compliance — issuers, not officers
    const supervisorCounts: OfficerReportCount[] = stats?.supervisorReportCounts ?? []

    const metCount = hasMinimum ? supervisorCounts.filter(s => s.reportCount >= minimum).length : null
    const notMetCount = metCount !== null ? supervisorCounts.length - metCount : null

    const sortedSupervisors = useMemo(() => {
        const counts = stats?.supervisorReportCounts ?? []
        return [...counts].sort((a, b) => {
            if (hasMinimum && prioritiseNotMet) {
                const aMet = a.reportCount >= minimum ? 1 : 0
                const bMet = b.reportCount >= minimum ? 1 : 0
                if (aMet !== bMet) return aMet - bMet
            }
            return a.nick.localeCompare(b.nick)
        })
    }, [stats, hasMinimum, prioritiseNotMet, minimum])

    if (isLoading) {
        return <Grid container spacing={2}>
            {[...Array(8)].map((_, i) => <Grid item xs={6} sm={3} key={i}><Skeleton variant="rectangular" height={72}/></Grid>)}
        </Grid>
    }

    if (!stats) return null

    return <Grid container spacing={2} alignItems="flex-start">
        {/* ── Controls row ── */}
        <Grid item xs={12}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <TextField
                    label="Min. reports per supervisor"
                    type="number"
                    size="small"
                    sx={{width: 210}}
                    value={minimum || ""}
                    onChange={e => onMinimumChange(Math.max(0, parseInt(e.target.value) || 0))}
                    inputProps={{min: 0}}
                />
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon/>}
                    onClick={onExport}
                    disabled={exportDisabled}
                >
                    Export CSV
                </Button>
            </Stack>
        </Grid>

        {/* ── Stat cards ── */}
        <Grid item xs={12} md={5}>
            <Grid container spacing={1}>
                <Grid item xs={4}>
                    <StatCard label="Total Reports" value={String(stats.totalReports)}/>
                </Grid>
                <Grid item xs={4}>
                    <StatCard label="Officers" value={String(stats.uniqueOfficers)} tooltip="Unique officers observed"/>
                </Grid>
                <Grid item xs={4}>
                    <StatCard label="Supervisors" value={String(stats.uniqueSupervisors)} tooltip="Unique supervisors who filed"/>
                </Grid>

                {hasMinimum && metCount !== null && notMetCount !== null && <>
                    <Grid item xs={6}>
                        <StatCard label="Met minimum" value={String(metCount)} color="success.main"
                            tooltip={`Supervisors who filed ≥ ${minimum} reports`}/>
                    </Grid>
                    <Grid item xs={6}>
                        <StatCard
                            label="Not met"
                            value={String(notMetCount)}
                            color={notMetCount > 0 ? "error.main" : "success.main"}
                            tooltip={`Supervisors who filed < ${minimum} reports`}
                        />
                    </Grid>
                </>}

                <Grid item xs={12}><Divider/></Grid>

                <Grid item xs={6}>
                    <StatCard label="Avg Leadership" value={fmt(stats.avgLeadershipScore)} tooltip="Out of 5"/>
                </Grid>
                <Grid item xs={6}>
                    <StatCard label="Avg Communication" value={fmt(stats.avgCommunicationScore)} tooltip="Out of 5"/>
                </Grid>
                <Grid item xs={6}>
                    <StatCard label="Avg Investigative" value={fmt(stats.avgInvestigativeScore)} tooltip="Out of 5"/>
                </Grid>
                <Grid item xs={6}>
                    <StatCard label="Avg Knowledge" value={fmt(stats.avgKnowledgeScore)} tooltip="Out of 5"/>
                </Grid>
                <Grid item xs={6}>
                    <StatCard label="Avg Rank Score" value={fmt(stats.avgRankScore)} tooltip="Out of 4"/>
                </Grid>
                <Grid item xs={6}>
                    <StatCard label="Avg Total" value={fmt(stats.avgTotalScore)} tooltip="Sum of all avg scores"/>
                </Grid>
            </Grid>
        </Grid>

        {/* ── Supervisor compliance table ── */}
        <Grid item xs={12} md={7}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{mb: 1}}>
                <Typography variant="subtitle2">
                    Supervisor report counts {hasMinimum && `(min. ${minimum})`}
                </Typography>
                {hasMinimum && (
                    <Chip
                        size="small"
                        label="Not met first"
                        color={prioritiseNotMet ? "warning" : "default"}
                        variant={prioritiseNotMet ? "filled" : "outlined"}
                        onClick={() => onPrioritiseNotMetChange(!prioritiseNotMet)}
                        sx={{cursor: "pointer"}}
                    />
                )}
            </Stack>
            <Box sx={{maxHeight: 320, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1}}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Supervisor</TableCell>
                            <TableCell align="right">Reports filed</TableCell>
                            {hasMinimum && <TableCell align="center">Requirement</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedSupervisors.length === 0
                            ? <TableRow><TableCell colSpan={3} align="center" sx={{color: "text.secondary", py: 3}}>No data</TableCell></TableRow>
                            : sortedSupervisors.map(s => {
                                const met = hasMinimum ? s.reportCount >= minimum : null
                                return <TableRow
                                    key={s.communityId}
                                    sx={met === false
                                        ? {backgroundColor: "rgba(211,47,47,0.10)"}
                                        : met === true
                                            ? {backgroundColor: "rgba(46,125,50,0.06)"}
                                            : undefined
                                    }
                                >
                                    <TableCell sx={{fontWeight: met === false ? 600 : undefined}}>
                                        {s.nick}
                                    </TableCell>
                                    <TableCell align="right">{s.reportCount}</TableCell>
                                    {hasMinimum && (
                                        <TableCell align="center">
                                            {met
                                                ? <CheckCircleOutlineIcon fontSize="small" color="success"/>
                                                : <CancelOutlinedIcon fontSize="small" color="error"/>
                                            }
                                        </TableCell>
                                    )}
                                </TableRow>
                            })
                        }
                    </TableBody>
                </Table>
            </Box>
        </Grid>
    </Grid>
}
